import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { rgba } from 'polished'
import React, { ChangeEvent, ComponentType, ReactNode } from 'react'
import { ComponentProps } from 'react'

import { overrideOptional } from '@emico/overrides'
import { theme } from '@emico/styles'

import Radio from './Radio'

// This radio is hidden but is used to handle the state.
const hiddenElementCss = css`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`

interface VisualRadioProps {
  size?: number
  borderSize?: number
  inactiveColor?: string
  activeColor?: string
  gutter?: number
  innerSize?: number
}

interface VisualRadioProps {
  disabled?: boolean
}

export const createVisualRadio = ({
  size = 16,
  borderSize = 2,
  inactiveColor = theme.gray600,
  activeColor = theme.primaryColor,
  gutter = 2,
  innerSize = size - gutter * 2 - borderSize * 2,
}: VisualRadioProps = {}) => styled.div<VisualRadioProps>`
  /* radio knob sizing */
  width: ${size}px;
  height: ${size}px;

  /* radio knob styling */
  border: solid ${borderSize}px ${inactiveColor};
  border-radius: ${size / 2}px;
  box-sizing: border-box;
  background: ${theme.backgroundColor};

  /* Layout, using flex to center the inside of the knob */
  display: inline-flex;
  justify-content: center;
  align-items: center;

  /* Spacing around the knob */
  position: relative;
  margin-left: 2px;
  margin-right: 2px;
  top: -${borderSize}px;
  vertical-align: baseline;

  /* Inside of the knob */
  &::before {
    content: '';
    display: inline-block;
    width: ${innerSize}px;
    height: ${innerSize}px;
    background: ${activeColor};
    border-radius: ${innerSize / 2}px;
    position: relative;

    transform-origin: center center;
    transform: scale(0);
    transition: transform 0.2s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  ${(props) =>
    props.disabled
      ? css`
          opacity: 0.5;
          cursor: not-allowed;
        `
      : ``}

  input:checked + & {
    border-color: ${activeColor};

    &::before {
      transform: scale(1);
    }
  }

  input:focus + & {
    box-shadow: 0 0 0 3px ${rgba(activeColor, 0.2)};
  }

  input:active + & {
    ${(props) =>
      !props.disabled
        ? css`
            background: ${rgba(inactiveColor, 0.5)};
          `
        : ``}
  }
`

export const VisualRadio = overrideOptional(createVisualRadio())

export const StyledRadioLabel = overrideOptional(styled.label``)

export interface CustomRadioProps extends ComponentProps<typeof Radio> {
  name?: string
  value: string
  disabled?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  checked?: boolean
}

interface Props extends CustomRadioProps {
  label?: ReactNode
  className?: string
  labelClassName?: string
  radio?: ComponentType<CustomRadioProps>
}

const StyledRadio = ({
  disabled,
  label,
  className,
  'data-testid': testId,
  radio: RadioComponent = Radio,
  ...others
}: Props & {
  'data-testid'?: string
}) => (
  <StyledRadioLabel className={className} data-testid={testId}>
    <RadioComponent {...others} disabled={disabled} css={hiddenElementCss} />
    <VisualRadio disabled={disabled} />
    {label}
  </StyledRadioLabel>
)

export default overrideOptional(StyledRadio)
